import { useState, useEffect } from 'react';
import api from '../services/api';
import VisitorChart from '../components/VisitorChart';

const StatRow = ({ items }) => (
  <div className="admin-stat-row">
    {items.map((item, i) => (
      <div key={i} className="admin-stat-item">
        <span className="admin-stat-value">{item.value}</span>
        <span className="admin-stat-label">{item.label}</span>
      </div>
    ))}
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    trialing: '#f59e0b',
    active: '#10b981',
    cancelled: '#ef4444',
    past_due: '#ef4444',
    pending: '#8888a0',
    generated: '#3b82f6',
    approved: '#10b981',
    posted: '#4f46e5',
    auto_replied: '#8b5cf6',
    skipped: '#8888a0',
    failed: '#ef4444',
    pending_auto_reply: '#f59e0b',
  };
  return (
    <span className="admin-badge" style={{ background: colors[status] || '#8888a0' }}>
      {status}
    </span>
  );
};

function ScoreBadge({ score }) {
  const color = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <span style={{
      fontWeight: 700, fontSize: 13, color,
      background: `${color}15`, padding: '2px 8px',
      borderRadius: 4,
    }}>{score}</span>
  );
}

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [auditStats, setAuditStats] = useState(null);
  const [recentAudits, setRecentAudits] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [generatorStats, setGeneratorStats] = useState(null);
  const [generatorLeads, setGeneratorLeads] = useState([]);
  const [generatorLoading, setGeneratorLoading] = useState(true);
  const [airbnbStats, setAirbnbStats] = useState(null);
  const [airbnbAudits, setAirbnbAudits] = useState([]);
  const [airbnbLoading, setAirbnbLoading] = useState(true);
  const [standingStats, setStandingStats] = useState(null);
  const [standingChecks, setStandingChecks] = useState([]);
  const [standingLoading, setStandingLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const statsRes = await api.request('/admin/stats');
      setStats(statsRes);
    } catch (err) {
      setError(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }

    // Load audit data separately so it doesn't block main stats
    try {
      setAuditLoading(true);
      const [aStats, aRecent] = await Promise.all([
        api.request('/audit/admin/stats'),
        api.request('/audit/admin/recent'),
      ]);
      setAuditStats(aStats);
      setRecentAudits(aRecent);
    } catch {
      // Audit tracking is non-critical
    } finally {
      setAuditLoading(false);
    }

    // Load free generator data separately so it doesn't block main stats
    try {
      setGeneratorLoading(true);
      const [gStats, gLeads] = await Promise.all([
        api.request('/free-tools/admin/stats'),
        api.request('/free-tools/admin/leads'),
      ]);
      setGeneratorStats(gStats);
      setGeneratorLeads(gLeads);
    } catch {
      // Generator tracking is non-critical
    } finally {
      setGeneratorLoading(false);
    }

    // Load Airbnb listing audit data separately so it doesn't block main stats
    try {
      setAirbnbLoading(true);
      const [abStats, abAudits] = await Promise.all([
        api.request('/free-tools/airbnb-audit/admin/stats'),
        api.request('/free-tools/airbnb-audit/admin/leads?leads_only=false'),
      ]);
      setAirbnbStats(abStats);
      setAirbnbAudits(abAudits);
    } catch {
      // Airbnb audit tracking is non-critical
    } finally {
      setAirbnbLoading(false);
    }

    // Load Superhost standing data separately so it doesn't block main stats
    try {
      setStandingLoading(true);
      const [sStats, sChecks] = await Promise.all([
        api.request('/free-tools/standing/admin/stats'),
        api.request('/free-tools/standing/admin/leads?leads_only=false'),
      ]);
      setStandingStats(sStats);
      setStandingChecks(sChecks);
    } catch {
      // Standing tracking is non-critical
    } finally {
      setStandingLoading(false);
    }
  };

  if (loading) return <div className="page-loading">Loading admin dashboard...</div>;
  if (error) return <div className="form-error">{error}</div>;
  if (!stats) return null;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Admin</h1>
        <button className="btn btn-secondary" onClick={loadData} style={{ fontSize: '0.85rem' }}>
          Refresh
        </button>
      </div>

      <div className="admin-overview">
        <div className="admin-section">
          <h3 className="admin-section-title">Visitors, last 30 days</h3>
          <VisitorChart />
        </div>

        <div className="admin-section">
          <h3 className="admin-section-title">Users</h3>
          <StatRow items={[
            { value: stats.users.total, label: 'Total' },
            { value: stats.users.today, label: 'Today' },
            { value: stats.users.this_week, label: 'This week' },
            { value: stats.users.this_month, label: 'This month' },
          ]} />
          <div className="admin-sub-stats">
            {Object.entries(stats.users.subscriptions).map(([s, c]) => (
              <span key={s} className="admin-sub-stat">
                <StatusBadge status={s} /> {c}
              </span>
            ))}
          </div>
        </div>

        <div className="admin-section">
          <h3 className="admin-section-title">Reviews</h3>
          <StatRow items={[
            { value: stats.reviews.total, label: 'Total' },
            { value: stats.reviews.today, label: 'Today' },
            { value: stats.reviews.this_week, label: 'This week' },
            { value: stats.reviews.average_rating, label: 'Avg rating' },
          ]} />
        </div>

        <div className="admin-section">
          <h3 className="admin-section-title">AI Responses</h3>
          <StatRow items={[
            { value: stats.responses.generated, label: 'Generated' },
            { value: stats.responses.approved, label: 'Approved' },
            { value: stats.responses.posted, label: 'Posted' },
          ]} />
        </div>

        <div className="admin-section">
          <h3 className="admin-section-title">Platform</h3>
          <StatRow items={[
            { value: stats.businesses.total, label: 'Businesses' },
            { value: stats.businesses.autopilot_enabled, label: 'Autopilot on' },
            { value: stats.connections.total, label: 'Connections' },
          ]} />
        </div>

        {stats.reviews.statuses && Object.keys(stats.reviews.statuses).length > 0 && (
          <div className="admin-section">
            <h3 className="admin-section-title">Review pipeline</h3>
            <div className="admin-pipeline">
              {Object.entries(stats.reviews.statuses).map(([status, count]) => (
                <div key={status} className="admin-pipeline-item">
                  <StatusBadge status={status} />
                  <span className="admin-pipeline-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Tracking */}
        {!auditLoading && auditStats && (
          <div className="admin-section">
            <h3 className="admin-section-title">Free Review Analysis</h3>
            <StatRow items={[
              { value: auditStats.total_audits, label: 'Total audits' },
              { value: auditStats.today, label: 'Today' },
              { value: `${auditStats.today_remaining}/${auditStats.daily_cap}`, label: 'Remaining today' },
              { value: auditStats.avg_reputation_score || '-', label: 'Avg score' },
            ]} />
            <StatRow items={[
              { value: auditStats.this_week, label: 'This week' },
              { value: auditStats.this_month, label: 'This month' },
            ]} />
          </div>
        )}

        {!auditLoading && recentAudits.length > 0 && (
          <div className="admin-section">
            <h3 className="admin-section-title">Recent Audits</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Business</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Rating</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Reviews</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Score</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAudits.map((audit) => (
                    <tr key={audit.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                        {audit.business_name || 'Unknown'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {audit.rating ? (
                          <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                            {audit.rating.toFixed(1)} &#9733;
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', color: 'var(--text-light)' }}>
                        {audit.total_reviews || '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {audit.reputation_score ? <ScoreBadge score={audit.reputation_score} /> : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)', fontSize: 12 }}>
                        {formatDate(audit.created_at)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <a
                          href={audit.share_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 11, color: 'var(--green)', textDecoration: 'none',
                            fontWeight: 600,
                          }}
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Airbnb Listing Audits */}
        {!airbnbLoading && airbnbStats && (
          <div className="admin-section">
            <h3 className="admin-section-title">Airbnb Listing Audits</h3>
            <StatRow items={[
              { value: airbnbStats.total_audits, label: 'Total audits' },
              { value: airbnbStats.today, label: 'Today' },
              { value: `${airbnbStats.today_remaining}/${airbnbStats.daily_cap}`, label: 'Remaining today' },
              { value: airbnbStats.leads_captured, label: 'Lead emails' },
            ]} />
            <StatRow items={[
              { value: airbnbStats.this_week, label: 'This week' },
              { value: airbnbStats.marketing_consented, label: 'Marketing OK' },
              { value: airbnbStats.lead_capture_rate != null ? `${airbnbStats.lead_capture_rate}%` : '-', label: 'Capture rate' },
            ]} />
          </div>
        )}

        {!airbnbLoading && airbnbAudits.length > 0 && (
          <div className="admin-section">
            <h3 className="admin-section-title">Recent Airbnb Audits</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Listing</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Email</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Rating</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Score</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Marketing OK</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {airbnbAudits.map((a) => (
                    <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.title || a.listing_url || 'Unknown'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)' }}>{a.email || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {a.rating ? (
                          <span style={{ color: '#f59e0b', fontWeight: 600 }}>{a.rating.toFixed(2)} &#9733;</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {a.score != null ? <ScoreBadge score={a.score} /> : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: a.marketing_consent ? 'var(--green)' : 'var(--text-light)' }}>
                        {a.email ? (a.marketing_consent ? 'Yes' : 'Not opted in') : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)', fontSize: 12 }}>
                        {formatDate(a.created_at)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <a
                          href={`https://stellarreply.com/airbnb-audit/r/${a.share_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Superhost Standing Checker */}
        {!standingLoading && standingStats && (
          <div className="admin-section">
            <h3 className="admin-section-title">Superhost Standing Checks</h3>
            <StatRow items={[
              { value: standingStats.total_checks, label: 'Total checks' },
              { value: standingStats.today, label: 'Today' },
              { value: `${standingStats.today_remaining}/${standingStats.daily_cap}`, label: 'Remaining today' },
              { value: standingStats.leads_captured, label: 'Lead emails' },
            ]} />
            <StatRow items={[
              { value: standingStats.this_week, label: 'This week' },
              { value: standingStats.below_superhost, label: 'Below 4.8' },
              { value: standingStats.marketing_consented, label: 'Marketing OK' },
              { value: standingStats.lead_capture_rate != null ? `${standingStats.lead_capture_rate}%` : '-', label: 'Capture rate' },
            ]} />
            {/* Reconstructions that failed to reproduce Airbnb's displayed rating.
                A rising count is the early warning that Airbnb's markup changed
                and the histogram parsing needs attention. */}
            {standingStats.unverified_reconstructions > 0 && (
              <div style={{
                marginTop: 12, padding: '10px 14px', borderRadius: 6, fontSize: 13,
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
              }}>
                <strong style={{ color: '#ef4444' }}>
                  {standingStats.unverified_reconstructions} unverified reconstruction
                  {standingStats.unverified_reconstructions === 1 ? '' : 's'}
                </strong>
                {' '}&mdash; these could not be checked against Airbnb&apos;s displayed rating. A
                rising count usually means Airbnb changed its markup and the star breakdown is no
                longer parsing.
              </div>
            )}
          </div>
        )}

        {!standingLoading && standingChecks.length > 0 && (
          <div className="admin-section">
            <h3 className="admin-section-title">Recent Standing Checks</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Listing</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Email</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Rating</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Superhost</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Verified</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Date</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {standingChecks.map((s) => (
                    <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.title || s.listing_url || 'Unknown'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)' }}>{s.email || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {s.reconstructed_mean != null ? (
                          <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                            {s.reconstructed_mean.toFixed(4)}
                          </span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600 }}>
                        {s.meets_superhost == null ? '-' : s.meets_superhost ? (
                          <span style={{ color: 'var(--green)' }}>Above</span>
                        ) : (
                          <span style={{ color: '#ef4444' }}>
                            {s.five_stars_needed != null ? `${s.five_stars_needed} to go` : 'Below'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: s.verified ? 'var(--green)' : '#ef4444' }}>
                        {s.verified ? 'Yes' : 'No'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)', fontSize: 12 }}>
                        {formatDate(s.created_at)}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <a
                          href={`https://stellarreply.com/standing/r/${s.share_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 11, color: 'var(--green)', textDecoration: 'none', fontWeight: 600 }}
                        >
                          View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Free Reply Generator */}
        {!generatorLoading && generatorStats && (
          <div className="admin-section">
            <h3 className="admin-section-title">Free Reply Generator</h3>
            <StatRow items={[
              { value: generatorStats.total_generations, label: 'Total replies' },
              { value: generatorStats.today, label: 'Today' },
              { value: `${generatorStats.today_remaining}/${generatorStats.daily_cap}`, label: 'Remaining today' },
              { value: generatorStats.unique_lead_emails, label: 'Lead emails' },
            ]} />
            <StatRow items={[
              { value: generatorStats.this_week, label: 'This week' },
              { value: generatorStats.this_month, label: 'This month' },
              { value: generatorStats.lead_capture_rate != null ? `${generatorStats.lead_capture_rate}%` : '-', label: 'Capture rate' },
            ]} />
          </div>
        )}

        {!generatorLoading && generatorLeads.length > 0 && (
          <div className="admin-section">
            <h3 className="admin-section-title">Generator Leads</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Business</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Type</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Rating</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Marketing OK</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-light)' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {generatorLeads.map((lead) => (
                    <tr key={lead.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>{lead.email}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)' }}>{lead.business_name || '-'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)' }}>{lead.business_type || '-'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {lead.star_rating ? (
                          <span style={{ color: '#f59e0b', fontWeight: 600 }}>{lead.star_rating} &#9733;</span>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: lead.marketing_consent ? 'var(--green)' : 'var(--text-light)' }}>
                        {lead.marketing_consent ? 'Yes' : 'Not opted in'}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-light)', fontSize: 12 }}>
                        {formatDate(lead.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
